import {
  CallbackPayload,
  ConnectWindowTimeout,
  SDKMode,
  WebsocketAcknowledgementMessageType,
  WebsocketHeartbeatInterval,
  WebsocketKeepaliveMessageType,
  WebsocketMessageIdField
} from "@shared-library";
import {NGXLogger} from "ngx-logger";
import {
  catchError,
  defer,
  filter,
  first,
  fromEvent,
  interval,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  TimeoutError,
  timer
} from "rxjs";
import {webSocket} from 'rxjs/webSocket';

// waitForWebsocketOrgConnectionOrTimeout will listen to a websocket server for events
// it will close if the ws server sends a close message, an error message or the timeout is reached.
// A successful connection will return a CallbackPayload
//
// This function must be ".subscribed()" to work. If not, the handler will not be registered and messages will be ignored.
// errors will be thrown if the connection is not completed during the timeout period. An error will also be thrown if the payload is an error.
// new Error('{"error":"error_code","error_description":"error message"}')
// Use `var errData = JSON.parse(err.toString())` parse the JSON payload from errors generated by this function.
//
// It would be ideal if we could also listen to window close events when the user manually closed the window.
// However, due to browser cross-origin limitations, it is not possible to do this.
// see: https://stackoverflow.com/questions/5712195/how-to-listen-for-child-window-closing


class FastenConnectionError extends Error {
  constructor(message: any) {
    super(message); // (1)
    this.name = "FastenConnectionError"; // (2)
  }
}

type WebsocketControlMessage = { type: string; message_id?: string };
type WebsocketMessage = CallbackPayload | WebsocketControlMessage;

function isWebsocketKeepaliveMessage(message: any): boolean {
  return message?.type === WebsocketKeepaliveMessageType;
}

function getWebsocketMessageId(message: any): string | undefined {
  const messageId = message?.[WebsocketMessageIdField];
  return typeof messageId === "string" && messageId.length > 0 ? messageId : undefined;
}

function waitForForegroundOrRetryDelay(): Observable<unknown> {
  if (typeof document !== "undefined" && document.hidden) {
    return fromEvent(document, "visibilitychange").pipe(
      filter(() => !document.hidden),
      first()
    );
  }

  return timer(1000);
}

function waitForWebsocketMessage(logger: NGXLogger, websocketUrl: URL): Observable<CallbackPayload> {
  return new Observable<CallbackPayload>((observer) => {
    const subject = webSocket<WebsocketMessage>(websocketUrl.toString());
    const subjectSubscription = subject.pipe(
      filter((message: any) => {
        if (isWebsocketKeepaliveMessage(message)) {
          logger.debug("websocket keepalive received")
          return false;
        }
        const messageId = getWebsocketMessageId(message);
        if (messageId) {
          logger.debug("websocket acknowledgement sent", messageId)
          subject.next({type: WebsocketAcknowledgementMessageType, message_id: messageId});
        }
        return true;
      }),
      map((message: any) => {
        logger.debug("websocket message received", message)

        //check if the message is an error
        if (message.error) {
          throw new FastenConnectionError(JSON.stringify(message))
        }

        return message as CallbackPayload
      }),
      first()
    ).subscribe(observer);

    // Send a keepalive immediately on subscribe so intermediaries with short idle
    // timeouts don't drop the socket before the first interval tick lands.
    logger.debug("websocket keepalive sent")
    subject.next({type: WebsocketKeepaliveMessageType});

    const heartbeatSubscription = interval(WebsocketHeartbeatInterval).subscribe(() => {
      logger.debug("websocket keepalive sent")
      subject.next({type: WebsocketKeepaliveMessageType});
    });

    return () => {
      heartbeatSubscription.unsubscribe();
      subjectSubscription.unsubscribe();
      subject.complete();
    };
  });
}

export function waitForWebsocketOrgConnectionOrTimeout(logger: NGXLogger, websocketUrl: URL, openedWindow: Window, sdkMode?: SDKMode):  Observable<CallbackPayload> {
  logger.info(`waiting for websocket notification from popup window`)

  return defer(() => waitForWebsocketMessage(logger, websocketUrl)).pipe(
    retry({
      delay: (err) => {
        if (err instanceof FastenConnectionError) {
          return throwError(() => err);
        }

        logger.warn("websocket connection interrupted, waiting to resume", err);
        return waitForForegroundOrRetryDelay();
      }
    }),
    timeout(ConnectWindowTimeout),
    catchError(err => {
      if (err instanceof TimeoutError) {
        logger.error("websocket connection timed out")
        //close the opened window
        if (openedWindow && !openedWindow.closed) {
          try {
            openedWindow.close()
          } catch (e) {
            logger.error("failed to close opened window after timeout", e)
          }
        }
        return throwError(() => new Error('{"error":"timeout","error_description":"The connection timed out waiting for user to complete authentication."}'))
      } else if (err instanceof FastenConnectionError) {
        logger.error("fasten connection error", err)
        return throwError(() => err)

      } else {
        logger.error("websocket connection error", err)
        return throwError(() => new Error(`{"error": "websocket_connection", "error_description": "An error occurred while communicating with Fasten server (code: '${err?.code}', reason: '${err?.reason}' )"}`))
      }
    })
  )
}
